import { useMemo } from "react";

import clsx from "clsx";

import { Select } from "../inputs";

import { Button } from "./button";

const PAGE_SIZE = [25, 50, 100];
const MAX_PAGES_TO_SHOW = 6;

interface Props {
  pageSize?: number;
  rowCount?: number;
  currentPage?: number;
  onChangePage?: (val: number) => void;
  onChangePageSize?: (val: number) => void;
}

export const Pagination = ({
  pageSize = 25,
  rowCount = 0,
  currentPage = 0,
  onChangePage,
  onChangePageSize,
}: Props) => {
  const totalPages = Math.ceil(rowCount / pageSize);

  const pages = useMemo(() => {
    const res = [];

    if (totalPages <= MAX_PAGES_TO_SHOW) {
      for (let i = 0; i < totalPages; i++) {
        res.push({ key: String(i), label: String(i + 1), index: i });
      }
    } else {
      if (currentPage < 3) {
        for (let i = 0; i < 4; i++) {
          res.push({ key: String(i), label: String(i + 1), index: i });
        }
        res.push({ key: "placeholder-next", label: "...", index: -1 });
        res.push({
          key: String(totalPages - 1),
          label: String(totalPages),
          index: totalPages - 1,
        });
      } else if (currentPage > totalPages - 4) {
        res.push({ key: "0", label: "1", index: 0 });
        res.push({ key: "placeholder-prev", label: "...", index: -1 });
        for (let i = totalPages - 4; i < totalPages; i++) {
          res.push({ key: String(i), label: String(i + 1), index: i });
        }
      } else {
        res.push({ key: "0", label: "1", index: 0 });
        res.push({ key: "placeholder-prev", label: "...", index: -1 });
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          res.push({ key: String(i), label: String(i + 1), index: i });
        }
        res.push({ key: "placeholder-next", label: "...", index: -1 });
        res.push({
          key: String(totalPages - 1),
          label: String(totalPages),
          index: totalPages - 1,
        });
      }
    }

    return res;
  }, [totalPages, currentPage]);

  return (
    <div className="text-sm flex items-center justify-between mr-0.5">
      <div className="flex items-center gap-2.5 text-v2-primary">
        Rows per page
        <Select
          value={pageSize}
          onChange={(val) => {
            if (val) {
              onChangePageSize?.(val);
            }
          }}
          options={PAGE_SIZE.map((size) => ({
            value: size,
            label: size.toString(),
          }))}
        />
        {currentPage * pageSize + 1}-
        {Math.min((currentPage + 1) * pageSize, rowCount)} of {rowCount}
      </div>
      <div className="flex items-center">
        <Button
          disabled={currentPage === 0}
          className={clsx("px-3 rounded-l-md text-v2-secondary", {
            "text-v2-gray/50": currentPage === 0,
          })}
          onClick={() => onChangePage?.(currentPage - 1)}
        >
          <i className="fa-regular fa-chevron-left" />
        </Button>
        {pages.map((page) => (
          <Button
            key={page.key}
            disabled={page.index === -1}
            active={page.index === currentPage}
            onClick={() => onChangePage?.(page.index)}
          >
            {page.label}
          </Button>
        ))}
        <Button
          disabled={currentPage === totalPages - 1}
          className={clsx("px-3 rounded-r-md text-v2-secondary", {
            "text-v2-gray/50": currentPage === totalPages - 1,
          })}
          onClick={() => onChangePage?.(currentPage + 1)}
        >
          <i className="fa-regular fa-chevron-right" />
        </Button>
      </div>
    </div>
  );
};
